/**
 * Sprint T-17.1 · Notification Center · HTTP service layer.
 *
 * 16 thin wrappers over /api/notifications/* (T-17 backend · frozen).
 * Zero business logic · zero data transformation · axios only.
 *
 * Consumers: `hooks/useNotifications.js` React Query hooks.
 */
import { api } from "../lib/api";

const BASE = "/notifications";

export const notificationsApi = {
  // 1. Dispatcher info (version, providers, dry_run, templates_count, retry_policies)
  info: () => api.get(`${BASE}/info`),

  // 2-3. Deliveries (listing + detail for drawer)
  listDeliveries: ({ status, channel, tenant_id, intent_id, limit = 50, skip = 0 } = {}) =>
    api.get(`${BASE}/deliveries`, {
      params: { status, channel, tenant_id, intent_id, limit, skip },
    }),
  getDelivery: (id) => api.get(`${BASE}/deliveries/${encodeURIComponent(id)}`),

  // 4-5. Intents (list + replay · superadmin)
  listIntents: ({ status, channel, tenant_id, limit = 50, skip = 0 } = {}) =>
    api.get(`${BASE}/intents`, { params: { status, channel, tenant_id, limit, skip } }),
  replayIntent: (id) => api.post(`${BASE}/intents/${encodeURIComponent(id)}/replay`),

  // 6-8. DLQ
  listDlq: ({ channel, limit = 50, skip = 0 } = {}) =>
    api.get(`${BASE}/dlq`, { params: { channel, limit, skip } }),
  retryDlq: (id) => api.post(`${BASE}/dlq/${encodeURIComponent(id)}/retry`),
  discardDlq: (id) => api.post(`${BASE}/dlq/${encodeURIComponent(id)}/discard`),

  // 9-11. Templates (registry + detail + overrides · T-18 upgrade: full CRUD)
  listTemplates: () => api.get(`${BASE}/templates`),
  getTemplate: (id) => api.get(`${BASE}/templates/${encodeURIComponent(id)}`),
  listOverrides: (id, tenant_id) =>
    api.get(`${BASE}/templates/${encodeURIComponent(id)}/overrides`, {
      params: { tenant_id },
    }),
  upsertOverride: (id, body) =>
    api.post(`${BASE}/templates/${encodeURIComponent(id)}/overrides`, body),
  deleteOverride: (id, overrideId) =>
    api.delete(`${BASE}/templates/${encodeURIComponent(id)}/overrides/${encodeURIComponent(overrideId)}`),

  // 12. Providers health
  providersHealth: () => api.get(`${BASE}/providers/health`),

  // 13-14. My preferences (superadmin edits their own)
  myPreferences: () => api.get(`${BASE}/preferences/me`),
  updateMyPreferences: (body) => api.put(`${BASE}/preferences/me`, body),

  // 15-16. Webhook endpoints (list + create + delete)
  listWebhookEndpoints: ({ tenant_id, active_only = true } = {}) =>
    api.get(`${BASE}/webhook-endpoints`, { params: { tenant_id, active_only } }),
  createWebhookEndpoint: (body) => api.post(`${BASE}/webhook-endpoints`, body),
  deleteWebhookEndpoint: (id) =>
    api.delete(`${BASE}/webhook-endpoints/${encodeURIComponent(id)}`),

  // ------------------------------------------------------------------
  // Sprint T-18 · Notification Studio · additive wrappers (superadmin)
  // ------------------------------------------------------------------
  studio: {
    // E-1 · render subject/body_html/body_text without persisting
    renderPreview: (body) => api.post(`${BASE}/studio/render-preview`, body),
    // E-2 · validate override fields (jinja + size + variables)
    validate: (body) => api.post(`${BASE}/studio/validate`, body),
    // E-3 · extract variables from arbitrary content
    extractVariables: (body) => api.post(`${BASE}/studio/extract-variables`, body),
    // E-4 · list playground history (global · Q7=b)
    playgroundHistory: (params = {}) =>
      api.get(`${BASE}/studio/playground-history`, { params }),
    // E-5 · persist a history entry
    savePlaygroundEntry: (body) => api.post(`${BASE}/studio/playground-history`, body),
    // E-6..E-9 · Analytics (T-18 Fase 4)
    analyticsSummary: (params) => api.get(`${BASE}/studio/analytics/summary`, { params }),
    analyticsThroughput: (params) =>
      api.get(`${BASE}/studio/analytics/throughput`, { params }),
    analyticsErrors: (params) => api.get(`${BASE}/studio/analytics/errors`, { params }),
    analyticsLatency: (params) => api.get(`${BASE}/studio/analytics/latency`, { params }),
    // E-10..E-11 · Legacy Migration
    legacySummary: (params) => api.get(`${BASE}/studio/legacy/summary`, { params }),
    legacyCallsites: () => api.get(`${BASE}/studio/legacy/callsites`),
  },

  // Test-Send (already exists in T-17.1 · reused by Playground · Q2=a strict self)
  testSend: (body) => api.post(`${BASE}/test-send`, body),
};

export default notificationsApi;
